import PropTypes from 'prop-types';
import React, { Children, cloneElement, forwardRef, Fragment, useCallback } from 'react';
import { ShowHideButton } from '../ShowHideButton/ShowHideButton';
import { ShowHideHidden } from '../ShowHideHidden/ShowHideHidden';
import { withUncontrolled } from './withUncontrolled';

/**
 * Disclosable content should be wrapped with `ShowHide.Hidden` to ensure expected behavior.
 * `ShowHide.Hidden` can be used in multiple places and all contents will be toggled with
 * button press. Only single child is allowed inside `ShowHide`, otherwise an error will be thrown.
 *
 * See `ShowHideWordCount` if the content is a string type and an automatic truncation based
 * on word count is preferred.
 *
 * W3C uses [`Disclosure`](https://www.w3.org/TR/wai-aria-practices-1.1/#disclosure)
 * to refer similar behavior.
 *
 * @subcomponents ['ShowHideWordCount']
 * @since 8.36.0
 */
export var ShowHide = withUncontrolled( /*#__PURE__*/forwardRef(function (_ref, forwardedRef) {
  var appearance = _ref.appearance,
    children = _ref.children,
    renderButton = _ref.renderButton,
    isHidden = _ref.isHidden,
    onClick = _ref.onClick;
  /**
   * EVENT HANDLER
   */
  var onClickCallback = useCallback(function () {
    if (onClick) {
      onClick(isHidden);
    }
  }, [isHidden, onClick]);

  /**
   * RENDER
   */

  // render toggle button
  var button = renderButton;
  if (typeof renderButton === 'function') {
    button = renderButton({
      appearance: appearance,
      isHidden: isHidden,
      ref: forwardedRef,
      onClick: onClickCallback
    });
  }

  // render children
  var content = Children.toArray(Children.only(children).props.children);
  content = content.map(function (child, index) {
    if (!child.type || child.type.displayName !== 'ShowHideHidden') {
      return child;
    }
    return /*#__PURE__*/cloneElement(child, Object.assign({}, child.props, {
      isHidden: isHidden,
      // eslint-disable-next-line zillow/react/no-array-index-key
      key: index
    }));
  });
  if (appearance === 'inline') {
    content = /*#__PURE__*/React.createElement(Fragment, null, content, isHidden ? " ..." : null, " ", " ", button);
  }
  var newChildren = /*#__PURE__*/cloneElement(children, Object.assign({}, children.props, {
    children: content
  }));
  return /*#__PURE__*/React.createElement(Fragment, null, newChildren, appearance === 'block' ? button : null);
}));
ShowHide.displayName = 'ShowHide';
ShowHide.propTypes = process.env.NODE_ENV !== "production" ? {
  /**
   * The type of ShowHide to display.
   */
  appearance: PropTypes.oneOf(['inline', 'block']).isRequired,
  /**
   * The visibility of disclosable content will be controlled by button press.
   */
  children: PropTypes.element.isRequired,
  /**
   * A ShowHide is uncontrolled by default.
   * You can specify `isHidden` to manually control the visibility of disclosable content.
   */
  // This prop will be added in withUncontrolled but will need to be exposed in the doc.
  defaultHidden: PropTypes.bool,
  /**
   * A ShowHide is uncontrolled by default.
   * You can specify `isHidden` to manually control the visibility of disclosable content.
   */
  isHidden: PropTypes.bool,
  /**
   * Function called when button is pressed.
   */
  onClick: PropTypes.func,
  /**
   * Default button styling corresponding to the `appearance` prop.
   */
  renderButton: PropTypes.oneOfType([PropTypes.node, PropTypes.func])
} : {};
ShowHide.defaultProps = {
  defaultHidden: true,
  renderButton: function renderButton(props) {
    return /*#__PURE__*/React.createElement(ShowHideButton, props, props.isHidden ? 'See More' : 'See Less');
  }
};
ShowHide.Hidden = ShowHideHidden;